// Местные и открытые источники на сайте: онлайн-табло аэропортов Узбекистана, новости и акции авиакомпаний
// (их Telegram-каналы), курсы покупки валюты в банках, стихийные бедствия рядом с городом поездки, сезоны умры
// и хаджа (исламский календарь), «куда едут» (интерес по просмотрам Википедии).
import { useEffect, useState } from 'react';
import type { AirlineNews, BankRates, BoardResult, BoardStatus, Hazards, HijriSeasons, Trending } from '@sayohat/shared';
import { api } from '../../shared/lib/api';
import { formatDay } from '../../shared/lib/format';
import { createStore, useStore } from '../../shared/lib/store';
import { Icon } from '../../shared/ui/Icon';
import { FlightTracker, trackFlight } from './opendata';
import './localdata.css';

export { EventsBlock, FlightTracker, TourismStatsBlock, trackFlight } from './opendata';

const AIRPORTS: [string, string][] = [
  ['TAS', 'Ташкент'], ['SKD', 'Самарканд'], ['BHK', 'Бухара'], ['UGC', 'Ургенч'], ['NCU', 'Нукус'], ['FEG', 'Фергана'], ['NMA', 'Наманган'], ['AZN', 'Андижан'], ['KSQ', 'Карши'], ['TMJ', 'Термез'], ['NVI', 'Навои'],
];
export const STATUS_LABEL: Record<BoardStatus, string> = {
  scheduled: 'По расписанию', checkin: 'Регистрация', boarding: 'Посадка', delayed: 'Задерживается', departed: 'Вылетел', arrived: 'Прибыл', cancelled: 'Отменён', unknown: '—',
};
const STATUS_CLASS: Record<BoardStatus, string> = { scheduled: '', checkin: 'blue', boarding: 'blue', delayed: 'warn', departed: 'good', arrived: 'good', cancelled: 'bad', unknown: '' };

/** Загрузка один раз за открытие страницы */
function useOnce<T>(url: string | null): { data: T | null; error: boolean } {
  const [s, set] = useState<{ data: T | null; error: boolean }>({ data: null, error: false });
  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    set({ data: null, error: false });
    api<T>(url).then((data) => { if (alive) set({ data, error: false }); }).catch(() => { if (alive) set({ data: null, error: true }); });
    return () => { alive = false; };
  }, [url]);
  return s;
}

// ---------- Онлайн-табло ----------

const board = createStore({ airport: 'TAS', type: 'departure' as 'departure' | 'arrival', q: '' });

export function AirportBoard() {
  const { airport, type, q } = useStore(board);
  const [all, setAll] = useState(false);
  const { data, error } = useOnce<BoardResult>(`/api/local/board?airport=${airport}&type=${type}`);
  const now = new Date();
  const hm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const today = now.toISOString().slice(0, 10);
  const s = q.trim().toLowerCase().replace(/\s+/g, '');
  const list = (data?.flights || [])
    .filter((f) => !s || f.flight.toLowerCase().includes(s) || f.city.toLowerCase().includes(s))
    // Сначала ближайшие: не улетевшие сегодня от текущего часа
    .filter((f) => s || all || f.date > today || (f.date === today && (f.estimated || f.time) >= hm.slice(0, 2) + ':00') || f.status === 'delayed')
    .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
  const shown = all || s ? list : list.slice(0, 12);
  return (
    <section className="block ld-board" id="airport-board">
      <div className="block-head">
        <h2><Icon name="takeoff" /> Онлайн-табло аэропортов Узбекистана</h2>
        <div className="ld-board-tools">
          <select value={airport} onChange={(e) => board.set({ airport: e.target.value })} aria-label="Аэропорт">
            {AIRPORTS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
          <div className="seg">
            <button type="button" className={type === 'departure' ? 'on' : ''} onClick={() => board.set({ type: 'departure' })}>Вылет</button>
            <button type="button" className={type === 'arrival' ? 'on' : ''} onClick={() => board.set({ type: 'arrival' })}>Прилёт</button>
          </div>
          <input type="search" value={q} onChange={(e) => board.set({ q: e.target.value })} placeholder="Рейс или город" aria-label="Рейс или город" />
        </div>
      </div>
      {error ? <p className="muted small">Табло аэропорта сейчас не отвечает — попробуйте позже.</p> : !data ? <p className="muted small">Загружаем табло…</p> : shown.length ? (
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead><tr><th>Время</th><th>{type === 'departure' ? 'Куда' : 'Откуда'}</th><th>Рейс</th><th>Терминал</th><th>{type === 'departure' ? 'Регистрация / выход' : ''}</th><th>Статус</th></tr></thead>
            <tbody>{shown.map((f) => (
              <tr key={`${f.date}${f.flight}${f.time}`} className={f.status === 'cancelled' ? 'dim' : ''}>
                <td className="nowrap"><b>{f.time}</b>{f.estimated && f.estimated !== f.time ? <div className="warn small">{`→ ${f.estimated}`}</div> : null}{f.date !== today ? <div className="muted small">{formatDay(f.date, { day: 'numeric', month: 'short' })}</div> : null}</td>
                <td><b data-no-i18n="">{f.city}</b> <span className="muted small">{f.code}</span></td>
                <td className="nowrap"><span data-no-i18n="">{f.flight}</span><div className="muted small" data-no-i18n="">{f.airline}</div>
                  {f.status === 'departed' || (type === 'arrival' && f.status !== 'arrived' && f.status !== 'cancelled') ? <button type="button" className="ghost ld-row-track" onClick={() => { trackFlight(f.flight); document.getElementById('flight-tracker')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}><Icon name="plane" />Где самолёт</button> : null}</td>
                <td>{f.terminal || '—'}</td>
                <td className="small">{type === 'departure' ? [f.checkIn ? `стойки ${f.checkIn}` : '', f.gate ? `выход ${f.gate}` : ''].filter(Boolean).join(' · ') || '—' : ''}</td>
                <td><span className={`pill ${STATUS_CLASS[f.status]}`.trim()}>{STATUS_LABEL[f.status]}{f.status === 'delayed' && f.estimated ? ` до ${f.estimated}` : ''}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <p className="muted small">Рейсов не нашлось.</p>}
      <div className="ld-foot">
        {list.length > shown.length ? <button type="button" className="ghost small-btn" onClick={() => setAll(true)}>{`Все рейсы (${list.length})`}</button> : <span />}
        <span className="muted small">Данные онлайн-табло uzairports.com, обновляются каждые 3 минуты.</span>
      </div>
      <FlightTracker />
    </section>
  );
}

// ---------- Новости авиакомпаний ----------

export function AirlineNewsBlock({ limit = 6, title = 'Новости и акции авиакомпаний' }: { limit?: number; title?: string }) {
  const { data } = useOnce<AirlineNews>('/api/local/airline-news');
  if (!data || !data.posts.length) return null;
  return (
    <section className="block ld-news" id="airline-news">
      <div className="block-head"><h2><Icon name="zap" /> {title}</h2><span className="muted small">Из официальных Telegram-каналов</span></div>
      <div className="ld-news-grid">
        {data.posts.slice(0, limit).map((p) => (
          <a key={p.url} className="ld-post" href={p.url} target="_blank" rel="noopener">
            {p.image ? <img src={p.image} alt="" loading="lazy" /> : null}
            <div className="ld-post-body">
              <div className="ld-post-head"><b data-no-i18n="">{p.airline}</b>{p.tags.map((t) => <span key={t} className="pill hot small">{t}</span>)}<span className="muted small">{formatDay(p.at.slice(0, 10), { day: 'numeric', month: 'short' })}</span></div>
              <p data-no-i18n="">{p.text.length > 220 ? `${p.text.slice(0, 220)}…` : p.text}</p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

// ---------- Курсы банков ----------

/** Где выгоднее купить валюту перед поездкой (банк продаёт клиенту) и где продать (банк покупает) */
export function BankRatesCard({ currency }: { currency: string }) {
  const cur = currency.toUpperCase();
  const { data } = useOnce<BankRates>(['USD', 'EUR', 'RUB', 'GBP', 'KZT'].includes(cur) ? '/api/local/bank-rates' : null);
  const r = data?.rates.find((x) => x.currency === cur);
  if (!r || !r.sell.length) return null;
  const fmt = (n: number) => `${n.toLocaleString('ru-RU')} сум`;
  return (
    <div className="ld-banks">
      <b><Icon name="coins" /> {`Купить ${cur} в банках Узбекистана`}</b>
      <ul>{r.sell.slice(0, 3).map((b) => <li key={b.slug}><span data-no-i18n="">{b.bank}</span><b>{fmt(b.rate)}</b></li>)}</ul>
      {r.buy[0] ? <p className="muted small">{`Продать дороже всего: `}<span data-no-i18n="">{r.buy[0].bank}</span>{` — ${fmt(r.buy[0].rate)}`}</p> : null}
      <p className="muted small">Курсы bank.uz, обновляются каждые 30 минут.</p>
    </div>
  );
}

// ---------- Стихийные бедствия ----------

export function HazardsNote({ lat, lon }: { lat: number | null; lon: number | null }) {
  const { data } = useOnce<Hazards>(lat != null && lon != null ? `/api/local/hazards?lat=${lat}&lon=${lon}` : null);
  if (!data || !data.events.length) return null;
  return (
    <div className="ld-hazards">
      <b><Icon name="alert" /> Сейчас в регионе</b>
      <ul>{data.events.slice(0, 3).map((e) => (
        <li key={e.url + e.at} className={e.level}><span>{e.kind}</span> <span data-no-i18n="">{e.title}</span> <span className="muted small">{`· ${e.km} км · ${formatDay(e.at.slice(0, 10), { day: 'numeric', month: 'short' })}`}</span> <a href={e.url} target="_blank" rel="noopener">Подробнее</a></li>
      ))}</ul>
      <p className="muted small">{data.events.some((e) => e.source === 'ReliefWeb') ? 'По данным GDACS (ООН), USGS и ReliefWeb (OCHA). Перед поездкой уточните обстановку.' : 'По данным систем оповещения GDACS (ООН) и USGS. Перед поездкой уточните обстановку.'}</p>
    </div>
  );
}

// ---------- Сезоны умры и хаджа ----------

export function HijriBlock() {
  const { data } = useOnce<HijriSeasons>('/api/local/hijri');
  if (!data || !data.dates.length) return null;
  return (
    <section className="block ld-hijri">
      <div className="block-head"><h2><Icon name="moon-star" /> Сезоны паломничества</h2><span className="muted small">По исламскому календарю, ±1 день по наблюдению луны</span></div>
      <ul className="ld-hijri-list">
        {data.dates.map((d) => (
          <li key={d.title + d.date}><b>{formatDay(d.date, { day: 'numeric', month: 'long', year: 'numeric' })}</b><span>{d.title}</span></li>
        ))}
      </ul>
      <p className="muted small">В Рамадан и сезон хаджа цены на перелёты и отели у Харама заметно выше — бронируйте заранее.</p>
    </section>
  );
}

// ---------- Куда едут ----------

const MONTHS = ['январе', 'феврале', 'марте', 'апреле', 'мае', 'июне', 'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре'];

export function TrendingBlock({ onPick }: { onPick?: (code: string, name: string) => void }) {
  const { data } = useOnce<Trending>('/api/local/trending');
  if (!data || !data.places.length) return null;
  return (
    <section className="block ld-trend" id="trending">
      <div className="block-head"><h2><Icon name="up-trend" /> {`Куда едут в ${MONTHS[data.month - 1]}`}</h2><span className="muted small">По росту интереса в прошлые годы</span></div>
      <div className="ld-trend-list">
        {data.places.map((p) => (
          <a key={p.code} className="ld-trend-item" href={`/r/TAS-${p.code}`} onClick={(e) => { if (onPick) { e.preventDefault(); onPick(p.code, p.name); } }}>
            <b data-no-i18n="">{p.name}</b>
            <span className={p.growth > 0 ? 'good' : 'muted'}>{p.growth > 0 ? `+${p.growth}%` : `${p.growth}%`}</span>
          </a>
        ))}
      </div>
    </section>
  );
}
